/**
 * タブ切り替え機能
 * @param {Object} options - 設定オプション
 * @param {string} [options.tabSelector='.js-tab'] - タブのセレクター
 * @param {string} [options.labelSelector='[data-tab-label]'] - タブラベルのセレクター
 * @param {string} [options.contentSelector='[data-tab-content]'] - タブコンテンツのセレクター
 * @param {string} [options.activeClass='is-active'] - アクティブ状態のクラス名
 * @param {number} [options.duration=400] - アニメーション時間（ミリ秒）
 * @returns {Object} - 制御メソッドを含むオブジェクト
 */
import { fadeIn, fadeOut } from "./fade";

export const tab = (options = {}) => {
  // デフォルトオプションとマージ
  const config = {
    tabSelector: ".js-tab",
    labelSelector: "[data-tab-label]",
    contentSelector: "[data-tab-content]",
    activeClass: "is_active",
    duration: 400,
    ...options,
  };

  // DOM要素の取得
  const tabs = document.querySelectorAll(config.tabSelector);
  if (!tabs.length) return;

  // タブの状態を管理するMap
  const state = new Map();

  /**
   * タブの切り替え処理
   * @param {HTMLElement} parent - タブの親要素
   * @param {HTMLElement} targetContent - 表示するコンテンツ要素
   * @returns {void}
   */
  const toggleTab = (parent, targetContent) => {
    const currentState = state.get(parent);
    if (!currentState) return;

    // すでにアクティブか、アニメーション中なら処理しない
    if (
      targetContent === currentState.activeContent ||
      currentState.isAnimating
    )
      return;

    // アニメーション中フラグをセット
    currentState.isAnimating = true;

    // すべてのトリガーとコンテンツに対して処理
    currentState.triggers.forEach((trigger, index) => {
      const content = currentState.contents[index];
      const isTarget = content === targetContent;

      // クラスの切り替え
      trigger.classList.toggle(config.activeClass, isTarget);
      content.classList.toggle(config.activeClass, isTarget);

      // 対象外のコンテンツはフェードアウト
      if (!isTarget && content === currentState.activeContent) {
        fadeOut(content, config.duration);
      }
    });

    // 以前のアクティブコンテンツを記録
    const previousContent = currentState.activeContent;

    // 新しいアクティブコンテンツを設定
    currentState.activeContent = targetContent;

    // 対象コンテンツをフェードイン
    fadeIn(targetContent, config.duration, {
      onComplete: () => {
        currentState.isAnimating = false;
      },
    });

    // 安全対策：タイムアウトでもアニメーションフラグをリセット
    setTimeout(() => {
      currentState.isAnimating = false;
    }, config.duration + 20);

    // アクセシビリティ対応：ARIA属性の更新
    currentState.triggers.forEach((trigger, index) => {
      const content = currentState.contents[index];
      const isSelected = content === targetContent;
      trigger.setAttribute("aria-selected", isSelected.toString());
      content.setAttribute("aria-hidden", (!isSelected).toString());
    });

    // カスタムイベントを発火
    parent.dispatchEvent(
      new CustomEvent("tab:change", {
        detail: {
          previousContent,
          currentContent: targetContent,
        },
      })
    );
  };

  /**
   * タブの初期化処理
   * @param {HTMLElement} tabElement - タブ要素
   * @returns {void}
   */
  const setupTab = (tabElement) => {
    // ラベルとコンテンツの取得
    const labelsContainer = tabElement.querySelector(config.labelSelector);
    const contentsContainer = tabElement.querySelector(config.contentSelector);

    // 必要な要素がない場合は処理しない
    if (!labelsContainer || !contentsContainer) {
      console.error("タブのラベルまたはコンテンツが見つかりません", tabElement);
      return;
    }

    const triggers = Array.from(labelsContainer.children);
    const contents = Array.from(contentsContainer.children);

    // トリガーとコンテンツの数が一致しない場合
    if (triggers.length !== contents.length) {
      console.error("タブのトリガーとコンテンツの数が一致しません", tabElement);
      return;
    }

    // 初期状態の設定
    const initialState = {
      activeContent: null,
      isAnimating: false,
      triggers,
      contents,
    };

    // アクティブなタブを探す
    let activeIndex = triggers.findIndex((trigger) =>
      trigger.classList.contains(config.activeClass)
    );

    // アクティブなタブがない場合は最初のタブをアクティブに
    if (activeIndex === -1) activeIndex = 0;

    // 初期状態としてアクティブなコンテンツを設定
    initialState.activeContent = contents[activeIndex];

    // すべてのコンテンツを非表示にしてから、アクティブなものだけ表示
    contents.forEach((content, i) => {
      if (i !== activeIndex) {
        content.classList.remove(config.activeClass);
        fadeOut(content, 0);
        content.setAttribute("aria-hidden", "true");
      } else {
        content.classList.add(config.activeClass);
        content.style.opacity = 1;
        content.setAttribute("aria-hidden", "false");
      }
    });

    // トリガーのアクセシビリティ設定
    triggers.forEach((trigger, i) => {
      trigger.setAttribute("role", "tab");
      trigger.setAttribute("aria-selected", (i === activeIndex).toString());
      trigger.setAttribute("tabindex", i === activeIndex ? "0" : "-1");

      // キーボード操作のサポート
      trigger.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleTab(tabElement, contents[i]);
        }
      });

      // クリックイベントの設定
      trigger.addEventListener("click", () => {
        toggleTab(tabElement, contents[i]);
      });
    });

    // コンテンツのアクセシビリティ設定
    contents.forEach((content) => {
      content.setAttribute("role", "tabpanel");
    });

    // 状態を保存
    state.set(tabElement, initialState);
  };

  // 各タブの初期化
  tabs.forEach(setupTab);

  // 公開メソッドを返す
  return {
    // 特定のタブを外部から操作するためのメソッド
    activateTab: (tabElement, index) => {
      const currentState = state.get(tabElement);
      if (currentState && currentState.contents[index]) {
        toggleTab(tabElement, currentState.contents[index]);
      }
    },
    // クリーンアップ用メソッド
    destroy: () => {
      tabs.forEach((tabElement) => {
        const currentState = state.get(tabElement);
        if (!currentState) return;

        // イベントリスナーの削除
        currentState.triggers.forEach((trigger) => {
          trigger.removeEventListener("click", null);
          trigger.removeEventListener("keydown", null);
        });

        // 状態の削除
        state.delete(tabElement);
      });
    },
  };
};
